import { PageChangeEvent, Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import {
  toolbarPlugin,
  ToolbarSlot,
  TransformToolbarSlot,
} from "@react-pdf-viewer/toolbar";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import React, { useEffect, useState } from "react";

import {
  pageNavigationPlugin,
  RenderGoToPageProps,
} from "@react-pdf-viewer/page-navigation";

import { IonButton } from "@ionic/react";
import { PDFDocument } from "pdf-lib";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.8.162/pdf.worker.min.js";

const getPDFTrunckated = async (pdfPath: string, pageNumbers: number[]) => {
  const mergedPdf = await PDFDocument.create();

  for (const pageNumber of pageNumbers) {
    const pdfBytes = await fetch(pdfPath).then((res) => res.arrayBuffer());
    const sourcePdf = await PDFDocument.load(pdfBytes);
    const [copiedPage] = await mergedPdf.copyPages(sourcePdf, [pageNumber]);
    mergedPdf.addPage(copiedPage);
  }

  return mergedPdf.save();
};

const getNumberArr = (
  initialValue: number,
  endPage: number,
  excludeEndPage: boolean
) => {
  const arr = [];

  const lastPage = excludeEndPage ? endPage : endPage + 1;
  for (let i = initialValue; i < lastPage; i++) {
    arr.push(i - 1);
  }

  return arr;
};

interface Props {
  isItBook?: boolean;
  fileURL: string;
  setBookLoader: (isLoad: boolean) => void;
  initialPage?: number;
  endPage?: number;
  goToTop?: boolean;
  label?: string;
  onHitLastPage?: () => void;
  getNbPage?: (page: number) => void;
}

const PDFViewer: React.FC<Props> = ({
  isItBook,
  fileURL,
  setBookLoader,
  initialPage,
  endPage,
  goToTop = true,
  label = "Sommaire",
  onHitLastPage = () => null,
  getNbPage = () => null,
}) => {
  const [defaultScale, setDefaultScale] = useState(2.4);

  useEffect(() => {
    getPDFPageCount(fileURL).then((res: number) => getNbPage(res));
    function handleResize() {
      const screenWidth = window.innerWidth;
      if (screenWidth < 600) {
        setDefaultScale(1.0);
      } else if (screenWidth >= 600 && screenWidth < 1024) {
        setDefaultScale(1.5);
      } else if (screenWidth >= 1024 && screenWidth < 1400) {
        setDefaultScale(1.8);
      } else {
        setDefaultScale(2.0);
      }
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function getPDFPageCount(pdfUrl: string): Promise<number> {
    try {
      const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
      return pdf.numPages;
    } catch (error) {
      return -1;
    }
  }

  const [PDF, setPDF] = useState<Uint8Array | string>();

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { GoToFirstPage } = pageNavigationPluginInstance;

  const toolbarPluginInstance = toolbarPlugin({
    pageNavigationPlugin: {
      enableShortcuts: true,
    },
  });
  const { renderDefaultToolbar, Toolbar } = toolbarPluginInstance;

  const transform: TransformToolbarSlot = (slot: ToolbarSlot) => ({
    ...slot,
    EnterFullScreen: () => <></>,
    Download: () => <></>,
    Print: () => <></>,
    Open: () => <></>,
    SwitchTheme: () => <></>,
    Search: () => <></>,
    ShowSearchPopover: () => {
      return goToTop === true ? (
        <GoToFirstPage>
          {(props: RenderGoToPageProps) => (
            <IonButton
              style={{
                "--border-color": "var(--ion-color-secondary)",
                "--border-style": "solid",
                "--border-width": "2px",
                "--color": "var(--ion-color-secondary)",
              }}
              shape="round"
              onClick={props.onClick}
            >
              {label}
            </IonButton>
          )}
        </GoToFirstPage>
      ) : (
        <></>
      );
    },
    OpenMenuItem() {
      return <></>;
    },
    DownloadMenuItem() {
      return <></>;
    },
    PrintMenuItem() {
      return <></>;
    },
    SwitchThemeMenuItem() {
      return <></>;
    },
    NumberOfPages() {
      return <></>;
    },
    CurrentPageInput() {
      return <></>;
    },
    // GoToFirstPage: () => <GoToFirstPage />,
  });

  useEffect(() => {
    if (initialPage !== undefined && endPage !== undefined) {
      const excludeEndPage = localStorage.getItem("techno") === "cuis";
      getPDFTrunckated(
        fileURL,
        getNumberArr(initialPage, endPage, excludeEndPage)
      ).then((res) => {
        setPDF(res);
      });
    } else {
      setPDF(fileURL);
    }
  }, [initialPage, endPage, fileURL]);

  const handleChangePage: (event: PageChangeEvent) => void = ({
    doc,
    currentPage,
  }) => {
    if (doc.numPages - 1 === currentPage) onHitLastPage();
  };

  return (
    <>
      {PDF ? (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.8.162/build/pdf.worker.min.js">
          <div
            className="js-viewer-container rpv-core__viewer"
            style={{
              border: "1px solid rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <div
              style={{
                alignItems: "center",
                backgroundColor: "#eeeeee",
                borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                display: "flex",
                padding: "0.25rem",
              }}
            >
              <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>
            </div>

            <div
              style={{
                flex: 1,
                overflow: "hidden",
              }}
            >
              <Viewer
                onPageChange={handleChangePage}
                defaultScale={defaultScale}
                enableSmoothScroll={false}
                fileUrl={PDF}
                onDocumentLoad={() => setBookLoader(false)}
                plugins={[toolbarPluginInstance, pageNavigationPluginInstance]}
              ></Viewer>
            </div>
          </div>
        </Worker>
      ) : (
        <div>Chargement</div>
      )}
    </>
  );
};

export default PDFViewer;
